import { Injectable } from '@angular/core';
import {
  HTTPWrapperContext,
  XHR_STRATEGIES,
  REQUEST_METHODS,
  RequestConfigImplementation
} from '@ptl/xhr';
import { NgRedux } from '@ptl/angular-redux-store';
import { StateStructure } from '@ptl/state';

import { PlanServiceResponse } from '../../interfaces/plan-service-response.interface';
import { PRODUCT_GROUPS, PRODUCT_GROUP_NAMES } from 'src/app/angular-elements/plan-selection/enums/product-groups.enum';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { PlansStateStructure } from '../../configs/interfaces/plansStateStructure.interface';
import { SeniorMarketsRetrievePlansPayload } from '../../services/interfaces/view-plans/non-medigap.request.interface';
import { MedigapRetrievePlansPayload, MedicareSupplementRetrievePlansPayload } from '../../services/interfaces/view-plans/medigap.request.interface';
import { ZipCountyStateStructure } from 'src/app/angular-elements/shared/interfaces/zipCountyStateStructure.interface';
import { EffectiveDateStateStructure } from '../../../shared/components/configs/interfaces/effective-date.state-structure.interface';
import { comparePlansByIds } from '../../configs/reducers/plan-details.reducer';
import { environment } from 'src/environments/environment';
import { PlanTypesCollection } from '../../interfaces/plan-types-collection.interface';
import { setRecommendedPlanIds, savePlans, plansNeedRefreshedFromBackend } from '../../configs/reducers/plans.reducer';
import { RecommendedPlans } from '../../interfaces/recommended-plans.interface';
import { DrxService } from 'src/app/angular-elements/medical-information/medication-information/services/drx.service';
import { County } from '../../../shared/interfaces/zip-county-response.interface';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class MedicarePlansService {

  planServiceUrl = '/retrievePlans/plans';
  planCache = {};

  constructor(
    private ngRedux: NgRedux<StateStructure>,
    private httpClient: HTTPWrapperContext,
    private reducerUtilityService: ReducerUtilityService,
    private drxService: DrxService
  ) { }

  createPayload(productGroup, planYear?: string, countyOverride?: County): SeniorMarketsRetrievePlansPayload {
    const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');
    const plansState: PlansStateStructure = this.reducerUtilityService.getState('Plans');

    let county = countyOverride ? countyOverride : zipCountyState.county;

    let payload: SeniorMarketsRetrievePlansPayload = {
      aspId: environment.initData.brand.ASPID,
      county: county?.county,
      year: this.getPayloadPlanYear(plansState, planYear),
      market: 'Senior',
      region: county?.region,
      usageCode: plansState.recommendPlansFlowUsageCode || 1
    }

    // productGroups check...
    if (productGroup === PRODUCT_GROUPS.MEDIGAP) {
      payload = this.prepareMedigapPayload(plansState, payload, zipCountyState);
    }

    if (productGroup === PRODUCT_GROUPS.MEDICARE_SUPPLEMENT) {
      payload = this.prepareMedicareSupplementPayload(plansState, payload);
    }

    return payload;
  }

  async getPlans(payload: SeniorMarketsRetrievePlansPayload | MedigapRetrievePlansPayload): Promise<PlanServiceResponse> {
    let cacheKey = JSON.stringify(payload);
    if (this.planCache[cacheKey]) {
      return this.planCache[cacheKey];
    }

    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: this.planServiceUrl, // `/retrievePlans/plans`
      POST_OR_PUT_PAYLOAD: payload
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.GOOGLE_CLOUD);
    let response = await this.httpClient.makeRequest<PlanServiceResponse>(requestConfig);

    this.planCache[cacheKey] = response;
    return response;
  }

  storePlans(plansResponse: PlanTypesCollection): void {
    this.ngRedux.dispatch(comparePlansByIds([]));
    this.ngRedux.dispatch(savePlans(plansResponse));
    this.ngRedux.dispatch(plansNeedRefreshedFromBackend(false));
  }

  storeRecommendedPlans(recommendedPlans: RecommendedPlans): void {
    if (recommendedPlans.recommendedPlan) {
      this.ngRedux.dispatch(setRecommendedPlanIds(recommendedPlans));
    }
  }

  postRetrievalHooks(combinedPlansResponse: PlanTypesCollection, combinedRecommendedPlans: RecommendedPlans[]) {
    const runDrxForProducts: PRODUCT_GROUP_NAMES[] = ['Medicare Advantage', 'Prescription Drug Plan']
    const arraysIntersect = function (haystack, arr) {
      return arr.some(function (v) {
        return haystack.includes(v);
      });
    };

    if (arraysIntersect(Object.keys(combinedPlansResponse), runDrxForProducts)) {
      this.drxService.updateSession().catch(e => console.error(e));
    }
  }

  getPayloadPlanYear(plansState: PlansStateStructure, planYear?: string): string {
    if (planYear) {
      return planYear;
    }
    else if (plansState.medigapValuesPostFormSubmit?.coverageDate){
      return moment(plansState.medigapValuesPostFormSubmit.coverageDate).year().toString();
    }
    else {
      const effectiveDateState: EffectiveDateStateStructure = this.reducerUtilityService.getReducerFromApplicationState('EffectiveDate');
      return effectiveDateState.effectiveDateYear;
    }
  }

  prepareMedigapPayload(plansState: PlansStateStructure, payload, zipCountyState) {
    let medigapPayload: MedigapRetrievePlansPayload = payload;

    
    // Special case for handling only NY regions
    if (
      (zipCountyState.county.region === 'WNY' || zipCountyState.county.region === 'NENY')
      && plansState.medigapValuesPostFormSubmit
      && plansState.medigapValuesPostFormSubmit.macra !== null
      && plansState.medigapValuesPostFormSubmit.dob !== null
      && plansState.medigapValuesPostFormSubmit.coverageDate !== null
    ) {
      medigapPayload.attributes = {
        product_group: 'Medigap',
        DOB: plansState.medigapValuesPostFormSubmit.dob,
        coverageStartDate: plansState.medigapValuesPostFormSubmit.coverageDate,
        macra: plansState.medigapValuesPostFormSubmit.macra,
      };
    }
    else if (
      plansState.medigapValuesPostFormSubmit
      && plansState.medigapValuesPostFormSubmit.gender !== null
      && plansState.medigapValuesPostFormSubmit.macra !== null
      && plansState.medigapValuesPostFormSubmit.tobaccoUsage !== null
      && plansState.medigapValuesPostFormSubmit.dob !== null
      && plansState.medigapValuesPostFormSubmit.coverageDate !== null
      && plansState.medigapValuesPostFormSubmit.whb !== null
      && plansState.productGroupsForCounty
      && ((plansState.productGroupsForCounty.Medigap && plansState.productGroupsForCounty.Medigap.length > 0))
    ) {
      medigapPayload.attributes = {
        product_group: 'Medigap',
        DOB: plansState.medigapValuesPostFormSubmit.dob,
        coverageStartDate: plansState.medigapValuesPostFormSubmit.coverageDate,
        gender: plansState.medigapValuesPostFormSubmit.gender,
        tobacco: plansState.medigapValuesPostFormSubmit.tobaccoUsage,
        whb: plansState.medigapValuesPostFormSubmit.whb,
        macra: plansState.medigapValuesPostFormSubmit.macra,
        hardStops: plansState.medigapValuesPostFormSubmit.hardStops
      };
      
      if (zipCountyState.county.state === 'PA') {
        // If 'flow' is populated it means the eligibility service returned successfully and we have values
        // We do not want to submit medigap payload unless the eligibility service has been completed for PA
        if (plansState.medigapValuesPostFormSubmit.flow !== null) {
          medigapPayload.attributes.flow = plansState.medigapValuesPostFormSubmit.flow;
        }
        else {
          delete medigapPayload.attributes;
        }
      }
    }

    return medigapPayload;
  }

  prepareMedicareSupplementPayload(plansState, payload) {
    let medicareSupplementPayload: MedicareSupplementRetrievePlansPayload;

    if (
      plansState.medigapValuesPostFormSubmit
      && plansState.medigapValuesPostFormSubmit.dob !== null
      && plansState.medigapValuesPostFormSubmit.gender !== null
      && plansState.medigapValuesPostFormSubmit.coverageDate !== null
      && plansState.productGroupsForCounty
      && (plansState.productGroupsForCounty['Medicare Supplement']
        && plansState.productGroupsForCounty['Medicare Supplement'].length > 0)
    ) {
      medicareSupplementPayload = payload;
      medicareSupplementPayload.attributes = {
        product_group: 'Medicare Supplement',
        DOB: plansState.medigapValuesPostFormSubmit.dob,
        gender: plansState.medigapValuesPostFormSubmit.gender,
        coverageStartDate: plansState.medigapValuesPostFormSubmit.coverageDate,
        hardStops: plansState.medigapValuesPostFormSubmit.hardStops
      };
    }

    return medicareSupplementPayload || payload;
  }
}
